import type { ComponentRenderProps } from "@json-render/react";
import { CropIcon } from "@phosphor-icons/react";
import { createContext, type ReactNode, useContext } from "react";

import { ReportBannerImage } from "@/components/ui/report/ReportBannerImage/ReportBannerImage";
import { cn } from "@/lib/utils";

type ReportImagePropsFromSpec = {
  banner?: boolean | null;
  src?: string;
  alt?: string;
  caption?: string | null;
  rounded?: boolean | null;
  maxHeight?: number | null;
};

/**
 * Lets an editor offer Crop on the banners it can crop, and Edit (crop and resize) on body
 * Images, which pass `resize`. Unset, Images render without either.
 */
export const ReportImageCropContext = createContext<{
  canCrop: (src: string) => boolean;
  onCrop: (image: { src: string; alt: string; resize?: boolean }) => void;
} | null>(null);

const CroppableImage = ({
  src,
  alt,
  resize,
  className,
  children,
}: {
  src: string;
  alt: string;
  /** Body Images only; banners always fill the page width. */
  resize?: boolean;
  className?: string;
  children: ReactNode;
}) => {
  const crop = useContext(ReportImageCropContext);
  if (crop == null || !crop.canCrop(src)) return children;
  const action = resize ? "Edit" : "Crop";

  return (
    <div className={cn("group/crop relative", className)}>
      {children}
      <button
        type="button"
        onClick={() => crop.onCrop({ src, alt, resize })}
        aria-label={`${action} ${alt || "image"}`}
        className={cn(
          "absolute right-2 top-2 flex items-center gap-1 rounded-md bg-white/95 px-2 py-1",
          "text-xs font-medium text-gray-800 shadow-sm ring-1 ring-gray-200",
          "opacity-0 transition-opacity group-hover/crop:opacity-100 focus-visible:opacity-100",
          "hover:bg-white focus-visible:outline-2 focus-visible:outline-violet-600",
        )}
      >
        <CropIcon aria-hidden className="size-3.5" />
        {action}
      </button>
    </div>
  );
};

/**
 * Adapter for the Template Studio Image. Every Image shows the whole image in its own shape:
 * flagged banners fill the page width, ignoring Herald's chat-sized maxHeight; other Images
 * keep their natural size, scaled down to fit the column and maxHeight but never cropped.
 */
export const ReportImageFromProps = ({ element }: ComponentRenderProps) => {
  const { props } = element;
  const { banner, src, alt, caption, rounded, maxHeight } =
    props as ReportImagePropsFromSpec;

  if (banner === true) {
    return (
      <CroppableImage src={src ?? ""} alt={alt ?? ""}>
        <ReportBannerImage src={src ?? ""} alt={alt ?? ""} caption={caption} />
      </CroppableImage>
    );
  }
  if (!src) return null;

  return (
    <figure className="m-0 w-full">
      <CroppableImage
        src={src}
        alt={alt ?? ""}
        resize
        className="w-fit max-w-full"
      >
        <img
          src={src}
          alt={alt ?? ""}
          className={cn(
            "block h-auto w-auto max-w-full",
            rounded !== false && "rounded-lg",
          )}
          style={maxHeight != null ? { maxHeight } : undefined}
        />
      </CroppableImage>
      {caption != null && caption !== "" ? (
        <figcaption className="mt-1 text-xs text-muted-foreground">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
};
